import React from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import FilterListIcon from '@mui/icons-material/FilterList';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useTheme } from '@mui/material/styles';
import { MergeOptions } from '../Model/CostType';
import Toggle from '../../../Components/ui/Toggle';

interface MergeControlsProps {
  options: MergeOptions;
  onOptionsChange: (next: MergeOptions) => void;
  showVirtual: boolean;
  onShowVirtualChange: (next: boolean) => void;
}

const MergeControls: React.FC<MergeControlsProps> = ({
  options,
  onOptionsChange,
  showVirtual,
  onShowVirtualChange,
}) => {
  const theme = useTheme();

  const set = (key: keyof MergeOptions) => (val: boolean) =>
    onOptionsChange({ ...options, [key]: val });

  return (
    <Paper
      elevation={1}
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 1.5,
        px: 3,
        py: 2,
      }}
    >
      {/* Label */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 1 }}>
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'action.hover',
          }}
        >
          <FilterListIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
        </Box>
        <Typography
          variant="overline"
          color="text.secondary"
          sx={{ letterSpacing: '0.05em' }}
        >
          Merge into tenant:
        </Typography>
      </Box>

      {/* Divider */}
      <Box
        sx={{
          width: '1px',
          height: 24,
          background: theme.palette.divider,
          flexShrink: 0,
        }}
      />

      {/* Toggles */}
      <Toggle
        label="Farm Shared"
        checked={options.FarmShared}
        onChange={set('FarmShared')}
        color="warning"
      />
      <Toggle
        label="Regional Shared"
        checked={options.RegionalShared}
        onChange={set('RegionalShared')}
        color="success"
      />
      <Toggle
        label="Platform"
        checked={options.Platform}
        onChange={set('Platform')}
        color="info"
      />

      {/* Divider */}
      <Box
        sx={{
          width: '1px',
          height: 24,
          background: theme.palette.divider,
          flexShrink: 0,
          ml: 0.5,
        }}
      />

      {/* Virtual toggle */}
      <Box sx={{ ml: 'auto' }}>
        <Button
          size="small"
          variant={showVirtual ? 'outlined' : 'text'}
          onClick={() => onShowVirtualChange(!showVirtual)}
          startIcon={
            showVirtual ? (
              <VisibilityOffIcon sx={{ fontSize: '16px !important' }} />
            ) : (
              <VisibilityIcon sx={{ fontSize: '16px !important' }} />
            )
          }
          sx={{
            fontSize: 13,
            fontWeight: 500,
            height: 32,
            px: 2,
            borderRadius: '8px',
            color: showVirtual ? 'primary.main' : 'text.secondary',
          }}
        >
          {showVirtual ? 'Hide Virtual' : 'Show Virtual'}
        </Button>
      </Box>
    </Paper>
  );
};

export default MergeControls;
